import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Product, ProductType, getProductImageUrl, getProductImages } from '../../models/product/product';
import { Book, BookFormat } from '../../models/product/book';

interface ProductDetailsModalProps {
  product: Product;
  productType: ProductType;
  isOpen: boolean;
  onClose: () => void;
  onAddToCart: (format?: BookFormat) => void;
  isRTL: boolean;
  isAddingToCart?: (format?: BookFormat) => boolean;
}

export const ProductDetailsModal: React.FC<ProductDetailsModalProps> = ({
  product,
  productType,
  isOpen,
  onClose,
  onAddToCart,
  isRTL,
  isAddingToCart = () => false
}) => {
  const { t } = useTranslation();
  const [selectedFormat, setSelectedFormat] = useState<BookFormat | undefined>(
    productType === ProductType.BOOK
      ? (product as Book).bookFormat === BookFormat.BOTH 
        ? BookFormat.PHYSICAL 
        : (product as Book).bookFormat
      : undefined
  );
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [activeTab, setActiveTab] = useState<'description' | 'details'>('description'); // | 'reviews' - Hidden for now, will re-enable later
  const productImages = getProductImages(product);

  if (!isOpen) return null;

  const handleAddToCart = () => {
    if (productType === ProductType.BOOK && selectedFormat) {
      onAddToCart(selectedFormat);
    } else {
      onAddToCart();
    }
    // Don't close immediately if adding to cart - wait for completion
    if (!isAddingToCart(selectedFormat)) {
      onClose();
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Product-specific details rendering
  const renderProductSpecificDetails = () => {
    switch (productType) {
      case ProductType.BOOK:
        const book = product as Book;
        return (
          <>
            {/* Author Info */}
            <div className="mb-4">
              <h4 className="font-semibold text-gray-900 mb-2">
                {t('products.books.details.author', 'Author')}
              </h4>
              <p className="text-gray-700">{book.author}</p>
              {book.description && (
                <p className="text-sm text-gray-600 mt-2">{book.description}</p>
              )}
            </div>

            {/* Book Details Grid */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              {book.isbn && (
                <div>
                  <span className="text-sm font-medium text-gray-900">
                    {t('products.books.details.isbn', 'ISBN')}:
                  </span>
                  <span className="text-sm text-gray-700 ml-2">{book.isbn}</span>
                </div>
              )}
              {book.publisher && (
                <div>
                  <span className="text-sm font-medium text-gray-900">
                    {t('products.books.details.publisher', 'Publisher')}:
                  </span>
                  <span className="text-sm text-gray-700 ml-2">{book.publisher}</span>
                </div>
              )}
              {book.publishedDate && (
                <div>
                  <span className="text-sm font-medium text-gray-900">
                    {t('products.books.details.published', 'Published')}:
                  </span>
                  <span className="text-sm text-gray-700 ml-2">
                    {new Date(book.publishedDate).toLocaleDateString()}
                  </span>
                </div>
              )}
              {book.pages && (
                <div>
                  <span className="text-sm font-medium text-gray-900">
                    {t('products.books.details.pages', 'Pages')}:
                  </span>
                  <span className="text-sm text-gray-700 ml-2"> {book.pages} </span>
                </div>
              )}
              {book.language && (
                <div>
                  <span className="text-sm font-medium text-gray-900">
                    {t('products.books.details.language', 'Language')}:
                  </span>
                  <span className="text-sm text-gray-700 ml-2">
                    {t(`languages.${getLocalizedLanguageName(book.language)}`, getLanguageDisplayName(book.language))}
                  </span>
                </div>
              )}
              {/* {book.dimensions && (
                <div>
                  <span className="text-sm font-medium text-gray-900">
                    {t('products.books.details.dimensions', 'Dimensions')}:
                  </span>
                  <span className="text-sm text-gray-700 ml-2">
                    {book.dimensions.height} × {book.dimensions.width} × {book.dimensions.depth} cm
                  </span>
                </div>
              )} */}
              {book.translators && book.translators.length > 0 && (
                <div>
                  <span className="text-sm font-medium text-gray-900">
                    {book.translators.length === 1 
                      ? t('products.books.details.translator', 'Translator')
                      : t('products.books.details.translators', 'Translators')
                    }:
                  </span>
                  <span className="text-sm text-gray-700 ml-2">
                    {book.translators.map((translator, index) => translator.name).join(', ')}
                  </span>
                </div>
              )}
              {book.originalLanguage && (
                <div>
                  <span className="text-sm font-medium text-gray-900">
                    {t('products.books.details.original-language', 'Original Language')}:
                  </span>
                  <span className="text-sm text-gray-700 ml-2">
                    {t(`languages.${getLocalizedLanguageName(book.originalLanguage)}`, getLanguageDisplayName(book.originalLanguage))}
                  </span>
                </div>
              )}
              {book.originalTitle && (
                <div>
                  <span className="text-sm font-medium text-gray-900">
                    {t('products.books.details.original-title', 'Original Title')}:
                  </span>
                  <span className="text-sm text-gray-700 ml-2">{book.originalTitle}</span>
                </div>
              )}
            </div>

            {/* Format Selection */}
            {book.bookFormat === BookFormat.BOTH && (
              <div className="mb-6">
                <h4 className="font-semibold text-gray-900 mb-3">
                  {t('books.format.select', 'Select Format')}
                </h4>
                <div className="flex gap-3">
                  <button
                    onClick={() => setSelectedFormat(BookFormat.PHYSICAL)}
                    className={`flex-1 p-3 border-2 rounded-lg text-center transition-colors ${
                      selectedFormat === BookFormat.PHYSICAL
                        ? 'border-black bg-black text-white'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <div className="flex items-center justify-center space-x-2">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                      </svg>
                      <span>{t('books.format.physical', 'Physical Book')}</span>
                    </div>
                  </button>
                  <button
                    onClick={() => setSelectedFormat(BookFormat.DIGITAL)}
                    className={`flex-1 p-3 border-2 rounded-lg text-center transition-colors ${
                      selectedFormat === BookFormat.DIGITAL
                        ? 'border-black bg-black text-white'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <div className="flex items-center justify-center space-x-2">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      <span>{t('books.format.digital', 'Digital Book')}</span>
                    </div>
                  </button>
                </div>
              </div>
            )}

            {/* Table of Contents Preview - Show first 5 with Read More, or full list in details tab */}
            {book.tableOfContents && book.tableOfContents.length > 0 && (
              <div className="mb-4">
                <h4 className="font-semibold text-gray-900 mb-2">
                  {t('products.books.details.table-of-contents', 'Table of Contents')}
                </h4>
                <ul className={`text-sm text-gray-700 space-y-1 ${activeTab === 'details' ? 'max-h-96 overflow-y-auto' : ''}`}>
                  {(activeTab === 'details' ? book.tableOfContents : book.tableOfContents.slice(0, 5)).map((chapter, index) => (
                    <li key={`toc-${activeTab}-${index}`} className="flex">
                      <span className="font-medium mr-2">{index + 1}.</span>
                      <span>{chapter}</span>
                    </li>
                  ))}
                  {book.tableOfContents.length > 5 && activeTab !== 'details' && (
                    <li className="text-gray-500 mt-2">
                      <button
                        onClick={() => setActiveTab('details')}
                        className="text-black hover:text-gray-700 underline transition-colors"
                      >
                        {t('products.books.details.read-more', 'Read More')}
                      </button>
                    </li>
                  )}
                </ul>
              </div>
            )}
          </>
        );

      case ProductType.AUDIO:
        return (
          <div className="mb-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <span className="text-sm font-medium text-gray-900">
                  {t('products.audio.duration', 'Duration')}:
                </span>
                <span className="text-sm text-gray-700 ml-2">
                  {t('products.audio.duration-placeholder', 'Coming Soon')}
                </span>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-900">
                  {t('products.audio.format', 'Format')}:
                </span>
                <span className="text-sm text-gray-700 ml-2">MP3, WAV</span>
              </div>
            </div>
          </div>
        );

      case ProductType.VIDEO:
        return (
          <div className="mb-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <span className="text-sm font-medium text-gray-900">
                  {t('products.video.duration', 'Duration')}:
                </span>
                <span className="text-sm text-gray-700 ml-2">
                  {t('products.video.duration-placeholder', 'Coming Soon')}
                </span>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-900">
                  {t('products.video.quality', 'Quality')}:
                </span>
                <span className="text-sm text-gray-700 ml-2">HD, 4K</span>
              </div>
            </div>
          </div>
        );

      default:
        return (
          <div className="mb-4">
            <div className="text-sm text-gray-600">
              {t('products.details.category', 'Category')}: {product.category}
            </div>
          </div>
        );
    }
  };

  // Helper function to get localized language name
  const getLocalizedLanguageName = (languageCode: string): string => {
    const languageMap: { [key: string]: string } = {
      'en': 'ENGLISH',
      'ar': 'ARABIC'
    };
    return languageMap[languageCode] || languageCode.toUpperCase();
  };

  const getLanguageDisplayName = (languageCode: string): string => {
    const displayMap: { [key: string]: string } = {
      'en': 'English',
      'ar': 'Arabic'
    };
    return displayMap[languageCode] || languageCode;
  };

  // Format-specific badges
  const renderProductSpecificBadges = () => {
    if (productType === ProductType.BOOK) {
      // const book = product as Book;
      return (
        <>
          {/* Bestseller badge - commented out for future use */}
          {/* {book.bestseller && (
            <span className="bg-orange-500 text-white text-sm px-3 py-1 rounded-full font-medium shadow-lg">
              {t('general.badges.bestseller', 'Bestseller')}
            </span>
          )} */}
          {/* New Release badge - commented out for future use */}
          {/* {book.newRelease && (
            <span className="bg-green-500 text-white text-sm px-3 py-1 rounded-full font-medium shadow-lg">
              {t('general.badges.new', 'New Release')}
            </span>
          )} */}
        </>
      );
    }
    return null;
  };

  return (
    <div 
      className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-2 sm:p-4"
      onClick={handleBackdropClick}
    >
      <div 
        className="bg-white rounded-lg sm:rounded-2xl shadow-2xl max-w-6xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto"
        dir={isRTL ? 'rtl' : 'ltr'}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-4 sm:p-6 rounded-t-lg sm:rounded-t-2xl z-10">
          <div className={`flex items-center justify-between ${isRTL ? 'space-x-reverse' : ''}`}>
            <h2 className="text-2xl font-bold text-gray-900 line-clamp-1">
              {product.name}
            </h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors touch-manipulation min-w-[44px] min-h-[44px] flex items-center justify-center"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="p-4 sm:p-6">
          <div className={`grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 lg:gap-8 ${isRTL ? 'lg:grid-flow-col-dense' : ''}`}>
            {/* Left Column - Images */}
            <div className={`${isRTL ? 'lg:col-start-2' : ''}`}>
              {/* Main Image */}
              <div className="aspect-[3/4] mb-4 rounded-lg overflow-hidden bg-gray-100 relative">
                <img
                  src={productImages[selectedImageIndex] || getProductImageUrl(product)}
                  alt={product.name}
                  className="w-full h-full object-cover"
                />
                
                {/* Badges */}
                <div className={`absolute top-4 ${isRTL ? 'left-4' : 'right-4'} flex flex-col gap-2`}>
                  {/* Discount badge - commented out for future use */}
                  {/* {product.discount && product.discount > 0 && (
                    <span className="bg-red-500 text-white text-sm px-3 py-1 rounded-full font-medium shadow-lg">
                      -{product.discount}%
                    </span>
                  )} */}
                  {/* Featured badge - commented out for future use */}
                  {/* {product.featured && (
                    <span className="bg-blue-500 text-white text-sm px-3 py-1 rounded-full font-medium shadow-lg">
                      {t('general.badges.featured', 'Featured')}
                    </span>
                  )} */}
                  {renderProductSpecificBadges()}
                </div>
              </div>

              {/* Image Thumbnails */}
              {productImages.length > 1 && (
                <div className={`flex gap-2 overflow-x-auto pb-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  {productImages.map((image, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedImageIndex(index)}
                      className={`flex-shrink-0 w-16 h-20 rounded-lg overflow-hidden border-2 transition-colors ${
                        selectedImageIndex === index
                          ? 'border-black'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <img
                        src={image}
                        alt={`${product.name} ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Right Column - Product Info */}
            <div className={`${isRTL ? 'lg:col-start-1' : ''}`}>
              {/* Product Info */}
              <div className="mb-6">
                {/* Rating */}
                {/* <div className={`flex items-center mb-4 ${isRTL ? 'space-x-reverse' : ''} space-x-4`}>
                  <div className={`flex items-center ${isRTL ? 'space-x-reverse' : ''} space-x-1`}>
                    {[...Array(5)].map((_, i) => (
                      <svg
                        key={i}
                        className={`w-5 h-5 ${
                          i < Math.floor(product.rating) ? 'text-yellow-400' : 'text-gray-300'
                        }`}
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                    <span className="text-sm font-medium text-gray-900 ml-2">
                      {product.rating}
                    </span>
                  </div>
                  <span className="text-sm text-gray-600">
                    ({product.reviewsCount} {t('products.reviews', 'reviews')})
                  </span>
                </div> */}

                {/* Price */}
                <div className={`flex items-center mb-6 ${isRTL ? 'space-x-reverse' : ''} space-x-4`}>
                  <span className="text-3xl font-bold text-black">
                    {product.price.toFixed(0)} EGP
                  </span>
                  {/* Original price - commented out with discount functionality */}
                  {/* {product.originalPrice && product.originalPrice > product.price && (
                    <span className="text-xl text-gray-500 line-through">
                      {product.originalPrice.toFixed(0)} EGP
                    </span>
                  )} */}
                </div>

                {/* Stock Status - Hidden for now, will re-enable later */}
                {/* <div className="mb-6">
                  {product.inStock ? (
                    <div className={`flex items-center ${isRTL ? 'space-x-reverse' : ''} space-x-2 text-green-600`}>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="font-medium">
                        {t('products.status.in-stock', 'In Stock')} ({product.stockQuantity} {t('products.available', 'available')})
                      </span>
                    </div>
                  ) : (
                    <div className={`flex items-center ${isRTL ? 'space-x-reverse' : ''} space-x-2 text-red-600`}>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      <span className="font-medium">
                        {t('products.status.out-of-stock', 'Out of Stock')}
                      </span>
                    </div>
                  )}
                </div> */}

                {/* Product-specific details */}
                {renderProductSpecificDetails()}

                {/* Add to Cart Button */}
                <button
                  onClick={handleAddToCart}
                  disabled={isAddingToCart(selectedFormat)}
                  className="w-full bg-black text-white py-4 px-6 rounded-lg text-lg font-medium hover:bg-gray-800 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center space-x-3 mb-6"
                >
                  {isAddingToCart(selectedFormat) ? (
                    <>
                      <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>{t('cart.actions.adding-to-cart', 'Adding to Cart...')}</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4m1.6 8L5 3H3m4 10v6a1 1 0 001 1h10a1 1 0 001-1v-6M9 19a1 1 0 102 0 1 1 0 00-2 0zm8 0a1 1 0 102 0 1 1 0 00-2 0z" />
                      </svg>
                      <span>
                        {t('cart.actions.add-to-cart', 'Add to Cart')}
                      </span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Tabs Section */}
          <div className="border-t border-gray-200 mt-8 pt-8">
            {/* Tab Headers */}
            <div className={`flex border-b border-gray-200 mb-6 ${isRTL ? 'space-x-reverse' : ''}`}>
              <button
                onClick={() => setActiveTab('description')}
                className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
                  activeTab === 'description'
                    ? 'border-black text-black'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                {t('products.tabs.description', 'Description')}
              </button>
              <button
                onClick={() => setActiveTab('details')}
                className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
                  activeTab === 'details'
                    ? 'border-black text-black'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                {t('products.tabs.details', 'Details')}
              </button>
              {/* Reviews tab - Hidden for now, will re-enable later */}
              {/* <button
                onClick={() => setActiveTab('reviews')}
                className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
                  activeTab === 'reviews'
                    ? 'border-black text-black'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                {t('products.tabs.reviews', 'Reviews')} ({product.reviewsCount})
              </button> */}
            </div>

            {/* Tab Content */}
            <div className="min-h-[200px]">
              {activeTab === 'description' && (
                <div className="prose prose-gray max-w-none">
                  <p className="text-gray-700 leading-relaxed">
                    {product.fullDescription || product.description || t('products.no-description', 'No description available.')}
                  </p>
                </div>
              )}

              {activeTab === 'details' && (
                <div className="space-y-4">
                  {renderProductSpecificDetails()}
                  
                  {/* Tags */}
                  {/* {product.tags.length > 0 && (
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">
                        {t('products.details.tags', 'Tags')}
                      </h4>
                      <div className={`flex flex-wrap gap-2 ${isRTL ? 'space-x-reverse' : ''}`}>
                        {product.tags.map((tag, index) => (
                          <span
                            key={index}
                            className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  )} */}
                </div>
              )}

              {/* Reviews content - Hidden for now, will re-enable later */}
              {/* {activeTab === 'reviews' && (
                <div className="text-gray-600">
                  <p>{t('products.reviewsSection.coming-soon', 'Reviews section coming soon...')}</p>
                </div>
              )} */}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetailsModal;